import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ChannelCard } from '../../components/ai/ChannelCard';
import { BriefModal } from '../../components/ai/BriefModal';
import { CtaFooter } from '../../components/ai/CtaFooter';
import { getCMSPage, CMSPageContent } from '../../services/api';

/**
 * intrnauts.ai Contact — channels-front-door + brief modal (sketch 004 C).
 *
 * CMS-overridable:
 * - `intro_text` → lede paragraph
 * - `email`      → email-channel meta value + mailto target for direct channel
 *
 * Hardcoded (per CONTEXT.md — CMS gaps not extended this phase):
 * - All three channel headlines + meta grids
 * - The four-stat strip
 * - The "no newsletters" colophon
 * - All modal copy (locked voice rules)
 *
 * Per checker B1 fix: the featured "Book a 30-min call" card is a real
 * mailto: link — mailto IS the fastest channel, hence the mono numeral
 * label (rank 1) stays unchanged.
 */

// Pre-encoded subject/body for the "Book a call" mailto.
const CALL_MAILTO = 'mailto:hello@intrnauts.ai?subject=Call%20request%20%E2%80%94%2030%20min&body=Hi%20%E2%80%94%20I%27d%20like%20to%20book%20a%2030-minute%20call.%20Some%20times%20that%20work%20for%20me%3A%20';

const ContactAi: React.FC = () => {
  const [content, setContent] = useState<CMSPageContent | null>(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const briefTriggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    getCMSPage('contact').then(res => {
      if (res.data) setContent(res.data);
    });
  }, []);

  const fields = content?.fields ?? {};
  const email = fields.email?.trim() || 'hello@intrnauts.ai';

  const openBrief = () => setBriefOpen(true);
  const closeBrief = () => {
    setBriefOpen(false);
    requestAnimationFrame(() => {
      briefTriggerRef.current?.focus();
    });
  };

  return (
    <>
      <section className="ai-contact-page">
        <div className="eyebrow">● Contact — pick a channel</div>
        <h1 className="ai-contact-h1">
          Three ways in.<br />
          <i>One inbox.</i>
        </h1>
        <p className="ai-contact-lede">
          {fields.intro_text
            ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fields.intro_text) }} />
            : "Pick whichever channel matches the shape of your question. Whatever you choose, a real person reads it and replies within a business day."}
        </p>

        <div className="ai-channels">
          <ChannelCard
            featured
            num="// 01 — fastest"
            headline={<>Book a <i>30-min call.</i></>}
            description="If you'd rather talk than type. Best when the question is fuzzy and you want to think out loud with someone."
            meta={[
              { k: 'When',   v: 'Next 2 weeks' },
              { k: 'Cost',   v: 'Free intro' },
              { k: 'Length', v: '30 min' },
              { k: 'Format', v: 'Video' },
            ]}
            ctaLabel="Email to book →"
            href={CALL_MAILTO}
          />

          <ChannelCard
            ref={briefTriggerRef}
            num="// 02 — most useful"
            headline={<>Send a <i>brief.</i></>}
            description="A short form. Best when you have something specific in mind and want a considered written reply before a call."
            meta={[
              { k: 'Effort',  v: '~3 min' },
              { k: 'Reply',   v: '1 business day' },
              { k: 'Format',  v: 'Written' },
              { k: 'Privacy', v: 'Read by us only' },
            ]}
            ctaLabel="Open brief form →"
            onActivate={openBrief}
          />

          <ChannelCard
            num="// 03 — direct"
            headline={<>Just <i>email.</i></>}
            description="Old-school. Best when you already know what you want to say, or you'd rather not fill in any fields."
            meta={[
              { k: 'Address', v: email },
              { k: 'Reply',   v: '1 business day' },
              { k: 'Filter',  v: 'No auto-bots' },
              { k: 'Privacy', v: 'Just us' },
            ]}
            ctaLabel="Open mail client →"
            href={`mailto:${email}`}
          />
        </div>

        <div className="ai-strip" aria-label="Studio status">
          <div>
            <div className="ai-strip-k">— Currently</div>
            <div className="ai-strip-v"><i>Booking Q3</i></div>
          </div>
          <div>
            <div className="ai-strip-k">— Response</div>
            <div className="ai-strip-v">≤ 1 business day</div>
          </div>
          <div>
            <div className="ai-strip-k">— Timezone</div>
            <div className="ai-strip-v">UTC -8</div>
          </div>
          <div>
            <div className="ai-strip-k">— Capacity</div>
            <div className="ai-strip-v">1–2 engagements</div>
          </div>
        </div>

        <div className="ai-contact-foot">
          <p>
            No newsletters, no drip campaigns, no auto-sequenced follow-ups.
            If we don't reply, send the message again — sometimes things get missed, not ignored.
          </p>
        </div>
      </section>

      <CtaFooter
        headline={<>Tell us about <i>what you're trying to solve.</i></>}
        ctaLabel="Book intro call"
        ctaTo="/contact"
        direct={{ email, meta: 'UTC -8 · 1 business day reply' }}
      />

      <BriefModal isOpen={briefOpen} onClose={closeBrief} />
    </>
  );
};

export default ContactAi;
